import React, { useState } from 'react';
import { rateLimiter, getClientIdentifier, sanitizeErrorMessage } from '@/utils/securityUtils';
import { useCSRFProtection } from '@/hooks/useCSRFProtection';
import { toast } from 'sonner';

interface SecureFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void> | void;
  rateLimitKey?: string;
  maxAttempts?: number;
  rateLimitWindow?: number;
  children: React.ReactNode;
}

export const SecureForm: React.FC<SecureFormProps> = ({
  onSubmit,
  rateLimitKey,
  maxAttempts = 5,
  rateLimitWindow = 60000,
  children,
  ...formProps
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { csrfToken } = useCSRFProtection();

  const handleSecureSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (isSubmitting) {
      return;
    }

    // Rate limiting check
    const clientId = getClientIdentifier();
    const limitKey = rateLimitKey || `form-${clientId}-${window.location.pathname}`;
    
    if (!rateLimiter.isAllowed(limitKey, maxAttempts, rateLimitWindow)) {
      toast.error('Too many attempts. Please wait before trying again.');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(event);
    } catch (error) {
      const safeMessage = sanitizeErrorMessage(error);
      toast.error(safeMessage);
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSecureSubmit} {...formProps}>
      {csrfToken && (
        <input type="hidden" name="csrf_token" value={csrfToken} />
      )}
      {children}
    </form>
  );
};